import { Head } from "@aviv-unified-frontend/runtime/head";
import { useSearchParams } from "@aviv-unified-frontend/runtime/router";
import { useRef } from "react";
import type { ComponentType } from "react";

import { IS_RUNTIME_FEDERATION } from "../constants/environment";
import { useGlobalStore } from "../utils/useGlobalStore/useGlobalStore";

import { APP_VERION_KEY } from "./runtime/constants";
import { getApplicationScriptUrl } from "./utils/getApplicationScriptUrl";

/**
 * HOC that renders a component only once (first rendered is fixed).
 * For initial application script, this is to avoid re-injecting the script tag on route changes and fixed multiple loading issues.
 *
 * https://github.com/axel-springer-kugawana/aviv_platform_unified_frontend/pull/1968#discussion_r2334084664
 */
export const withRenderOnce = <P extends object>(
  Component: ComponentType<P>,
) => {
  return (props: P) => {
    const ref = useRef<React.ReactNode>(null);

    if (ref.current === null) {
      ref.current = <Component {...props} />;
    }

    return ref.current;
  };
};

export const InitialApplicationScript = withRenderOnce(() => {
  const [searchParams] = useSearchParams();
  const { applicationId } = useGlobalStore();

  const overloadedRuntimeVersion =
    searchParams.get(APP_VERION_KEY) || undefined;

  const url = getApplicationScriptUrl({
    id: applicationId,
    version: overloadedRuntimeVersion,
  });

  if (!url) return null;

  return (
    <Head>
      <link
        href={url}
        rel="preload"
        as="script"
      />
      {!IS_RUNTIME_FEDERATION && (
        <script
          defer
          src={url}
          onError={() => {
            document.head
              .querySelector(`script[src="${url}"]`)
              ?.setAttribute("data-error", "true");
          }}
        />
      )}
    </Head>
  );
});
